import React from 'react';
import {observer} from 'mobx-react-lite';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {CategoryCard} from '../CategoryCard';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Grid} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {SwitchCustom, TextBox} from '../../../Core/UI';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

type DependenceProps = {
    parameters: Approval;
    valueChanged: (p?: Parameter, value?: any) => void;
    setCategory: (entry: string) => void;
    index: number;
};

const Dependence = observer(({parameters, valueChanged, setCategory, index}: DependenceProps) => {
    return (
        <CategoryCard title={'Drug or Alcohol Dependence / Withdrawal'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        inputVariant="outlined"
                        variant="inline"
                        openTo="year"
                        views={['year', 'month']}
                        label={'When were you diagnosed?'}
                        style={{width: '100%', marginTop: 8}}
                        value={parameters.alcoholDiagnosisDate?.value || null}
                        onChange={(date) => valueChanged(parameters.alcoholDiagnosisDate, date)}
                    />
                </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'alcoholTreatment',
                        label: 'Received substance abuse treatment or detox?',
                        ...parameters.alcoholTreatment,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="alcoholTreatmentDetails"
                    label="Share treatment details received."
                    value={parameters.alcoholTreatmentDetails?.value}
                    onChange={(value) => valueChanged(parameters.alcoholTreatmentDetails, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        inputVariant="outlined"
                        variant="inline"
                        openTo="year"
                        views={['year', 'month']}
                        label={'Provide last treatment date'}
                        style={{width: '100%', marginTop: 8}}
                        value={parameters.alcoholTreatmentDate?.value || null}
                        onChange={(date) => valueChanged(parameters.alcoholTreatmentDate, date)}
                    />
                </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="alcoholTreatmentClinic"
                    label="Provide name of treatment clinic"
                    value={parameters.alcoholTreatmentClinic?.value}
                    onChange={(value) => valueChanged(parameters.alcoholTreatmentClinic, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="alcoholDrugFree"
                    label="How many years have you been alcohol or drug-free?"
                    type="number"
                    value={parameters.alcoholDrugFree?.value}
                    onChange={(value) => valueChanged(parameters.alcoholDrugFree, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'alcoholBeverages',
                        label: 'Do you currently use alcoholic beverages?',
                        ...parameters.alcoholBeverages,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'What type of alcohol do you drink?',
                        name: 'alcoholDrinkType',
                        options: fieldOptions.alcoholDrinkType.options,
                        ...parameters.alcoholDrinkType,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                    multiple
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'How many alcoholic drinks do you generally have per week?',
                        name: 'weeklyAlcoholicDrinks',
                        options: fieldOptions.weeklyAlcoholicDrinks.options,
                        ...parameters.weeklyAlcoholicDrinks,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="alcoholDrinks"
                    label="Add alcohol type and weekly drink count. (Deprecated)"
                    type="number"
                    value={parameters.alcoholDrinks?.value}
                    onChange={(value) => valueChanged(parameters.alcoholDrinks, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Are you currently an active member of?',
                        name: 'alcoholMember',
                        options: fieldOptions.alcoholMember.options,
                        ...parameters.alcoholMember,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="DrugOrAlcoholTreatmentDetailsNotes"
                    label="Notes"
                    value={parameters.DrugOrAlcoholTreatmentDetailsNotes?.value}
                    onChange={(value) => valueChanged(parameters.DrugOrAlcoholTreatmentDetailsNotes, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="typeOfDrug"
                    label="Type of Drug"
                    value={parameters.typeOfDrug?.value}
                    onChange={(value) => valueChanged(parameters.typeOfDrug, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'takingMedicineDueToDrug',
                        label: 'Are you currently taking any medication due to past dependence?',
                        ...parameters.takingMedicineDueToDrug,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.takingMedicineDueToDrug.value && (
                <Grid item xs={4}>
                    <TextBox
                        id="medicineNameDueToDrug"
                        label="List the name of medication and how long on medication"
                        value={parameters.medicineNameDueToDrug?.value}
                        onChange={(value) => valueChanged(parameters.medicineNameDueToDrug, value)}
                    />
                </Grid>
            )}
        </CategoryCard>
    );
});

export default Dependence;
